import { createCustomerDisplayBrand, createCustomerDisplayOrderContent, formatMeasuredQuantity, getMeasuredUnitLabel, parseCustomerDisplayLinePricing, resolveCustomerDisplayCurrency, type CustomerDisplayCalculatedOrder, type CustomerDisplayPaymentOverride } from '@washday/utilities';
import type { CustomerDisplayContent } from 'washday-sdk';
import { getOrderLineCharge } from '../pages/NewOrder/utils/minimumBillableQuantity';
import type { IStore } from '../interfaces/Store';

type DisplaySourceLine = Parameters<typeof getOrderLineCharge>[0] & {
  lineId?: string; orderProductLineID?: string; name?: string;
  selectedOption?: { label?: string } | null; properties?: string[];
};
export type WebDisplayOrder = CustomerDisplayCalculatedOrder & {
  products?: Array<DisplaySourceLine | string>; buyAndGetProducts?: Array<DisplaySourceLine | string>;
  express?: boolean; orderType?: 'standard' | 'return'; returnItemsChargeMode?: 'no_charge' | 'charge';
  currency?: string;
};

export function buildWebDisplayContent<T extends WebDisplayOrder>(
  order: T, store?: IStore | null, brand?: { name?: string; logoUrl?: string; country?: unknown }, paymentStatus?: CustomerDisplayPaymentOverride
): CustomerDisplayContent {
  const populated = (values: Array<DisplaySourceLine | string> = []) => values.filter((line): line is DisplaySourceLine => typeof line === 'object' && line !== null);
  const lines = [...populated(order.products).map((line) => ({ line, bonus: false })), ...populated(order.buyAndGetProducts).map((line) => ({ line, bonus: true }))].map(({ line, bonus }, index) => {
    const charge = getOrderLineCharge(line, Boolean(order.express), order);
    const quantity = Number(line.quantity);
    const measured = line.type === 'weight' || line.type === 'length';
    const unit = getMeasuredUnitLabel(line.type, store?.measurementUnits);
    // Web labels use quantity while legacy pricing can use qty. Omit an inconsistent breakdown.
    const pricing = Number.isFinite(quantity) && quantity > 0 && quantity === charge.actualQuantity
      ? parseCustomerDisplayLinePricing({
        quantityLabel: measured ? formatMeasuredQuantity(line.quantity) : String(line.quantity),
        unitPrice: charge.unitPrice,
        ...(unit ? { unit } : {}),
        ...(measured && charge.minimumApplied ? { minimumQuantityLabel: formatMeasuredQuantity(charge.billableQuantity) } : {}),
        ...(!bonus && line.discountAmount ? { unitPriceBeforeDiscount: charge.unitPriceBeforeDiscount } : {}),
      }) : null;
    return {
      id: line.lineId || line.orderProductLineID || `line-${index}`,
      name: line.name || '—',
      ...(line.selectedOption?.label ? { variant: line.selectedOption.label } : {}),
      quantity: line.quantity ?? line.qty ?? 0,
      amount: charge.total,
      kind: line.returnSourceOrderLineId ? 'return' as const : bonus ? 'bonus' as const : 'product' as const,
      ...(pricing ? { pricing } : {}),
    };
  });
  return {
    ...createCustomerDisplayOrderContent({ order, lines, taxesIncluded: store?.taxesType === 'included', paymentStatus }),
    brand: createCustomerDisplayBrand(brand, store),
    currency: resolveCustomerDisplayCurrency(order.currency, store?.country, brand?.country)
  };
}
